import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DemoGenieCloneService } from "@/services/demo/DemoGenieCloneService";
import { trackEvent } from "@/lib/ga4";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

type CreationStage = "initializing" | "creating" | "finalizing" | "success" | "error";

const STAGE_MESSAGES: Record<CreationStage, string> = {
  initializing: "Setting up your account...",
  creating: "Creating your genie...",
  finalizing: "Almost there...",
  success: "Your genie is ready!",
  error: "Something went wrong",
};

const CreatingGeniePage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<CreationStage>("initializing");
  const [errorMessage, setErrorMessage] = useState("");
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const hasPendingClone = DemoGenieCloneService.hasPendingClone();

    if (!hasPendingClone) {
      navigate("/dashboard", { replace: true });
      return;
    }

    runCloneFlow();
  }, []);

  const runCloneFlow = async (retryCount = 0) => {
    const params = DemoGenieCloneService.getPendingClone();
    if (!params) {
      navigate("/dashboard", { replace: true });
      return;
    }

    try {
      setStage("initializing");
      // Brief pause to let auth session fully propagate
      await delay(1500);

      setStage("creating");
      const result = await DemoGenieCloneService.cloneToUserAccount(params);

      setStage("finalizing");
      await delay(1000);

      DemoGenieCloneService.clearPendingClone();
      setStage("success");
      // Discover funnel: signup + clone completed for a demo persona (HG-0791).
      trackEvent("discover_signup_completed", { persona_id: params.persona_id });
      toast.success("Your genie is ready!");

      // Show success state briefly, then redirect
      await delay(2000);
      navigate("/genie-hub/my-genies", { replace: true });
    } catch (error: any) {
      console.error("[CreatingGenie] Clone failed:", error?.message || error);

      if (retryCount < 1) {
        console.log("[CreatingGenie] Retrying clone...");
        setStage("initializing");
        await delay(3000);
        return runCloneFlow(retryCount + 1);
      }

      DemoGenieCloneService.clearPendingClone();
      setStage("error");
      setErrorMessage(error?.message || "Failed to create your genie");
    }
  };

  const handleRetry = () => {
    setStage("initializing");
    setErrorMessage("");
    hasStarted.current = false;

    const hasPendingClone = DemoGenieCloneService.hasPendingClone();
    if (hasPendingClone) {
      runCloneFlow();
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const handleGoToDashboard = () => {
    DemoGenieCloneService.clearPendingClone();
    navigate("/dashboard", { replace: true });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, white 50%, ${SPARK_BLUE}08)`,
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full flex flex-col items-center text-center">
        {/* Animated ring / success check / error icon */}
        <div className="mb-8 relative flex items-center justify-center">
          {stage === "error" ? (
            <div className="w-32 h-32 rounded-full bg-red-50 flex items-center justify-center">
              <svg className="w-16 h-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          ) : stage === "success" ? (
            <div className="w-32 h-32 rounded-full flex items-center justify-center animate-scale-in" style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}20, ${SPARK_BLUE}20)` }}>
              <svg className="w-16 h-16" fill="none" stroke={SPARK_BLUE} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : (
            <div className="genie-spinner">
              <div className="genie-spinner-ring" />
              <div className="genie-spinner-icon">
                <img src="/helpgenie_logo.png" alt="" width={52} height={52} />
              </div>
            </div>
          )}
        </div>

        {/* Status message */}
        <h2
          className="text-2xl font-bold mb-2 transition-all duration-300"
          style={{
            background: stage === "error" ? undefined : `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
            WebkitBackgroundClip: stage === "error" ? undefined : "text",
            WebkitTextFillColor: stage === "error" ? undefined : "transparent",
            backgroundClip: stage === "error" ? undefined : "text",
            color: stage === "error" ? "#EF4444" : undefined,
          }}
        >
          {STAGE_MESSAGES[stage]}
        </h2>

        {/* Substatus */}
        {stage !== "error" && stage !== "success" && (
          <p className="text-gray-400 text-sm mb-6">This usually takes a few seconds</p>
        )}

        {/* Progress dots */}
        {stage !== "error" && stage !== "success" && (
          <div className="flex gap-2 mb-2">
            <div className="progress-dot" style={{ animationDelay: "0s" }} />
            <div className="progress-dot" style={{ animationDelay: "0.2s" }} />
            <div className="progress-dot" style={{ animationDelay: "0.4s" }} />
          </div>
        )}

        {/* Error state */}
        {stage === "error" && (
          <div className="mt-4 w-full space-y-3">
            <p className="text-sm text-gray-500 mb-4">{errorMessage}</p>
            <button
              onClick={handleRetry}
              className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})` }}
            >
              Try Again
            </button>
            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 px-4 rounded-xl font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Success state */}
        {stage === "success" && (
          <p className="text-gray-400 text-sm mt-2">Redirecting you now...</p>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        .genie-spinner {
          position: relative;
          width: 128px;
          height: 128px;
        }

        .genie-spinner-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid transparent;
          border-top-color: ${MAGENTA_BEAM};
          border-right-color: ${SPARK_BLUE};
          animation: spin 1.2s linear infinite;
        }

        .genie-spinner-ring::before {
          content: '';
          position: absolute;
          inset: 4px;
          border-radius: 50%;
          border: 2px solid transparent;
          border-bottom-color: ${MAGENTA_BEAM}60;
          border-left-color: ${SPARK_BLUE}60;
          animation: spin 2s linear infinite reverse;
        }

        .genie-spinner-icon {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse-soft 2s ease-in-out infinite;
        }

        .progress-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE});
          animation: dot-bounce 1.4s ease-in-out infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes pulse-soft {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(0.92); opacity: 0.8; }
        }

        @keyframes dot-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        @keyframes scale-in {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default CreatingGeniePage;
